import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "../utils/api";
import { PRESENCE_EVENT } from "./live-chat/AwayBar";

// Online / Away switch for live chat. While the admin app is open it sends a heartbeat, so the server knows the
// agent is reachable; new chats go only to agents who are Online and sent a heartbeat recently.
const HEARTBEAT_MS = 45000;

const PresenceSwitch = () => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const timerRef = useRef(null);

  const beat = useCallback(() => {
    api
      .post("/live-chat/presence/heartbeat", {}, { skipToast: true })
      .then((res) => res && res.status && setStatus(res.status))
      .catch(() => {});
  }, []);

  useEffect(() => {
    beat();
    timerRef.current = setInterval(beat, HEARTBEAT_MS);
    const onVisible = () => document.visibilityState === "visible" && beat();
    // the live chat screen's "Go online" button changes the status too
    const onPresence = (e) => e.detail && setStatus(e.detail);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener(PRESENCE_EVENT, onPresence);
    return () => {
      clearInterval(timerRef.current);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener(PRESENCE_EVENT, onPresence);
    };
  }, [beat]);

  if (!status) return null;
  const online = status === "online";

  const toggle = async () => {
    setBusy(true);
    try {
      const res = await api.put("/live-chat/presence", { status: online ? "away" : "online" });
      if (res && res.status) {
        setStatus(res.status);
        window.dispatchEvent(new CustomEvent(PRESENCE_EVENT, { detail: res.status }));
      }
    } catch (e) {
      // the API client shows the error
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      title={online ? "You receive new live chats. Click to go Away." : "You don't receive new live chats. Click to go Online."}
      className={`flex items-center gap-1.5 px-2 sm:px-2.5 py-1.5 rounded-lg border text-[11px] font-semibold transition-colors ${
        online
          ? "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
          : "border-zinc-200 bg-zinc-50 text-zinc-500 hover:bg-zinc-100"
      }`}
    >
      <span className={`w-2 h-2 rounded-full ${online ? "bg-emerald-500" : "bg-zinc-400"}`} />
      <span className="hidden sm:inline">{online ? "Online" : "Away"}</span>
    </button>
  );
};

export default PresenceSwitch;
